import React from 'react';
import dayjs from 'dayjs';
import type { MaterialIssuance, MaterialIssuanceItem } from '@/services/materialIssuance.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import { documentLinePieces, documentLineSize } from '@/utils/inventory/measurementQuantity';

interface PrintableMaterialIssueVoucherProps {
  issuance: MaterialIssuance;
  onClose?: () => void;
}

const toNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const fmtQty = (value: unknown): string =>
  toNumber(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtAmount = (value: unknown): string =>
  toNumber(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Material issue voucher — shared pagination engine with restored cost/trace columns. */
export const PrintableMaterialIssueVoucher: React.FC<PrintableMaterialIssueVoucherProps> = ({
  issuance,
  onClose,
}) => {
  const lines = issuance.items || [];
  const warehouseLabel = issuance.source_warehouse_summary
    ? `${issuance.source_warehouse_summary.warehouse_code || issuance.source_warehouse_summary.id} — ${issuance.source_warehouse_summary.warehouse_name || ''}`.trim()
    : issuance.source_warehouse_id || '';
  const projectLabel = issuance.project_summary
    ? [issuance.project_summary.project_number, issuance.project_summary.project_name]
        .filter(Boolean)
        .join(' — ')
    : '';
  const statusLabel = issuance.journal_voucher_summary?.voucher_number ? 'Posted' : 'Issued';

  return (
    <EngineCommercialA4Print
      docType="material_issue"
      title="MATERIAL ISSUE VOUCHER"
      documentNumber={issuance.issuance_number || 'MIV'}
      partyName={projectLabel}
      onClose={onClose}
      metaLines={[
        { label: 'Issuance #', value: issuance.issuance_number || '' },
        {
          label: 'Date',
          value: issuance.issuance_date
            ? dayjs(issuance.issuance_date).format('DD MMM YYYY HH:mm')
            : '',
        },
        { label: 'Status', value: statusLabel },
        {
          label: 'Material Request',
          value:
            issuance.materialRequest?.request_number ||
            String(issuance.material_request_id ?? ''),
        },
        {
          label: 'Work Order',
          value: issuance.work_order_number || String(issuance.work_order_id ?? ''),
        },
        { label: 'Project', value: projectLabel },
        { label: 'Warehouse', value: warehouseLabel },
        { label: 'Issued By', value: issuance.issued_by_name || '' },
        { label: 'Received By', value: issuance.received_by_name || '' },
        {
          label: 'JV #',
          value: issuance.journal_voucher_summary?.voucher_number || '',
        },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'code', label: 'Item Code' },
        { key: 'description', label: 'Item Name' },
        { key: 'boqLine', label: 'BOQ line' },
        { key: 'size', label: 'Size (L×W×H)' },
        { key: 'pcs', label: 'Pcs', align: 'right' },
        { key: 'qty', label: 'Qty', align: 'right' },
        { key: 'uom', label: 'UOM' },
        { key: 'unitCost', label: 'Unit Cost', align: 'right' },
        { key: 'totalCost', label: 'Total Cost', align: 'right' },
        { key: 'batch', label: 'Batch' },
        { key: 'heat', label: 'Heat' },
        { key: 'dimension', label: 'Dimension' },
      ]}
      lines={lines.map((line: MaterialIssuanceItem, index) => ({
        lineNumber: index + 1,
        description: line.item_name || '—',
        cells: {
          code: line.item_code || '—',
          boqLine: line.boq_line_label || '—',
          size: documentLineSize(line) || '—',
          pcs: documentLinePieces(line)?.toLocaleString(undefined, { maximumFractionDigits: 4 }) ?? '—',
          qty: fmtQty(line.issued_quantity),
          uom: line.uom || '—',
          unitCost: fmtAmount(line.unit_cost),
          totalCost: fmtAmount(line.total_cost),
          batch: line.batch_number || '—',
          heat: line.heat_number || '—',
          dimension: line.dimension || '—',
        },
      }))}
      finals={[
        { id: 'totals', heightPx: 36 },
        ...(issuance.notes ? [{ id: 'notes', heightPx: 40 }] : []),
      ]}
      finalBlocks={{
        totals: (
          <p>
            <strong>Total Issue Cost: {fmtAmount(issuance.total_cost)}</strong>
          </p>
        ),
        notes: issuance.notes ? (
          <div>
            <strong>Notes</strong>
            <p>{issuance.notes}</p>
          </div>
        ) : null,
      }}
    />
  );
};
